import axios from 'axios';

// ngrok's free tier serves an HTML "you're about to visit..." interstitial
// in place of the real response on every request until this header (or a
// per-domain cookie from clicking through it once in a browser) is present
// — without it, every XHR/fetch call gets back that HTML instead of JSON.
// Harmless to send against a non-ngrok backend; it's simply ignored there.
axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
export const API = `${BACKEND_URL}/api`;

export const getToken = () => localStorage.getItem('token');

export const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

// The service worker can't read localStorage, so mirror "is someone logged in"
// into Cache Storage. A push that arrives while this says '0' is for a stale
// subscription and the worker drops it instead of showing a notification.
export const writeSessionFlag = (loggedIn) => {
  try {
    if (typeof caches === 'undefined') return;
    caches.open('dotlink-session')
      .then((c) => c.put('/__session', new Response(loggedIn ? '1' : '0')))
      .catch(() => {});
  } catch {
    // storage unavailable — nothing to do
  }
};

export const setSession = (user, token) => {
  writeSessionFlag(true);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('token', token);
};

export const clearSession = () => {
  writeSessionFlag(false);
  localStorage.removeItem('user');
  localStorage.removeItem('token');
};

export const isLoggedIn = () => Boolean(getToken());

// Server-stored images (item photos, QR codes) come back as paths relative to
// the backend (e.g. "/static/uploads/x.jpg"). Blob/data/absolute URLs — used
// for local previews before upload finishes — are returned unchanged.
export const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }
  return `${BACKEND_URL}${url}`;
};

export const authHeaders = () => {
  const user = getUser();
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    'X-User-ID': user?.id || '',
    'Authorization': `Bearer ${token || ''}`,
  };
};

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = getToken();
  const user = getUser();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (user?.id) config.headers['X-User-ID'] = user.id;
  return config;
});

// Maintenance mode: the server answers 503 {maintenance: true}. Tell the site-config
// provider so the maintenance page appears immediately instead of after the next poll.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 503 && err.response?.data?.maintenance) {
      window.dispatchEvent(new Event('dl-maintenance'));
    }
    return Promise.reject(err);
  }
);
